import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';

/**
 * InlineTextEditor
 * 
 * A contentEditable component for inline text editing with keyboard shortcuts.
 * - Enter: Save changes
 * - Escape: Cancel and revert
 * - Blur: Save changes
 * 
 * @param {string} initialValue - The initial text value
 * @param {function} onSave - Callback when saving (receives new value)
 * @param {function} onCancel - Callback when cancelling
 * @param {boolean} multiline - Whether to allow multiline editing (default: false)
 * @param {boolean} isHTML - Whether content is HTML (preserves formatting during edit)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
const InlineTextEditor = ({
  initialValue = '',
  onSave,
  onCancel,
  multiline = false,
  isHTML = false,
  className = '',
  style = {},
  placeholder = 'Click to edit...',
}) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const originalValueRef = useRef(initialValue);

  const baseEditorClasses = [
    'outline-none',
    'focus:outline-none',
    'bg-transparent',
    'w-full',
    'caret-current'
  ]
    .filter(Boolean)
    .join(' ');

  // Focus and select all text when component mounts
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.focus();
      // Select all text for easy replacement
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, []);

  // Keep DOM content in sync with the latest initialValue only when not actively editing.
  useLayoutEffect(() => {
    if (!editorRef.current || isFocused) {
      return;
    }

    const nextValue = initialValue || '';
    if (isHTML) {
      editorRef.current.innerHTML = nextValue;
    } else {
      editorRef.current.textContent = nextValue;
    }
    originalValueRef.current = nextValue;
  }, [initialValue, isFocused, isHTML]);

  const getCurrentValue = () => {
    if (!editorRef.current) return '';
    // For HTML content, return innerHTML; for plain text, return textContent
    return isHTML ? editorRef.current.innerHTML : (editorRef.current.textContent || '');
  };

  const handleSave = () => {
    const currentValue = getCurrentValue();
    const trimmedValue = currentValue.trim();
    
    // Only save if value has changed
    if (trimmedValue !== originalValueRef.current?.trim()) {
      onSave(trimmedValue);
      originalValueRef.current = trimmedValue;
    } else {
      // No changes, just cancel
      onCancel();
    }
  };

  const handleCancel = () => {
    // Revert to original value
    if (editorRef.current) {
      if (isHTML) {
        editorRef.current.innerHTML = originalValueRef.current || '';
      } else {
        editorRef.current.textContent = originalValueRef.current || '';
      }
    }
    onCancel();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) {
      // Cmd+Enter or Ctrl+Enter saves in multiline mode
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Small delay to allow click events on buttons/controls to fire first
    setTimeout(() => {
      handleSave();
    }, 100);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handlePaste = (e) => {
    if (!isHTML) {
      // Prevent pasting HTML, only plain text for non-HTML editors
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    }
  };

  // Render with HTML or plain text
  const content = isHTML ? (
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onPaste={handlePaste}
      className={`${baseEditorClasses} ${className}`.trim()}
      style={{
        minHeight: multiline ? '60px' : 'auto',
        minWidth: '100px',
        maxWidth: '100%',
        ...style,
      }}
      data-placeholder={placeholder}
    />
  ) : (
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onPaste={handlePaste}
      className={`${baseEditorClasses} ${className}`.trim()}
      style={{
        minHeight: multiline ? '60px' : 'auto',
        minWidth: '100px',
        maxWidth: '100%',
        ...style,
      }}
      data-placeholder={placeholder}
    />
  );

  return content;
};

export default InlineTextEditor;

